import { DEFAULT_CATEGORIES } from "../data/defaults";
import { supabase } from "../lib/supabase";

export { supabase };

const today = () => new Date().toISOString().slice(0, 10);

export async function ensureProfileAndDefaults(user) {
  if (!user?.id) return;
  await supabase.from("profiles").upsert(
    {
      id: user.id,
      email: user.email,
      full_name: user.user_metadata?.full_name || "",
      default_currency: "USD",
      default_tax_year: new Date().getFullYear(),
      default_mileage_rate: 0.725,
    },
    { onConflict: "id" }
  );

  const { data: existing, error } = await supabase.from("categories").select("id").limit(1);
  if (error || existing?.length) return;
  const rows = DEFAULT_CATEGORIES.map(([name, schedule_e_category, color], index) => ({
    user_id: user.id,
    name,
    schedule_e_category,
    color,
    is_default: true,
    is_active: true,
    sort_order: (index + 1) * 10,
  }));
  await supabase.from("categories").insert(rows);
}

async function readTable(table, queryBuilder) {
  const { data, error } = await queryBuilder;
  if (error) throw error;
  return data || [];
}

function dedupeCategories(categories = []) {
  const grouped = categories.reduce((acc, category) => {
    const key = (category.name || "").trim().toLowerCase();
    if (!key) return acc;
    const current = acc[key];
    const currentScore = current ? Number(Boolean(current.is_active)) * 10 + Number(Boolean(current.is_default)) : -1;
    const nextScore = Number(Boolean(category.is_active)) * 10 + Number(Boolean(category.is_default));
    if (!current || nextScore > currentScore || (nextScore === currentScore && Number(category.sort_order || 9999) < Number(current.sort_order || 9999))) {
      acc[key] = category;
    }
    return acc;
  }, {});
  return Object.values(grouped).sort((a, b) => Number(a.sort_order || 9999) - Number(b.sort_order || 9999) || a.name.localeCompare(b.name));
}

export async function loadWorkspace(userId) {
  const [profileRows, properties, categories, vendors, expenses, mileage, income, receipts, lineItems] = await Promise.all([
    readTable("profiles", supabase.from("profiles").select("*").eq("id", userId).limit(1)),
    readTable("properties", supabase.from("properties").select("*").order("created_at", { ascending: false })),
    readTable("categories", supabase.from("categories").select("*").order("sort_order", { ascending: true })),
    readTable("vendors", supabase.from("vendors").select("*").order("name", { ascending: true })),
    readTable("expenses", supabase.from("expenses").select("*").order("expense_date", { ascending: false })),
    readTable("mileage_entries", supabase.from("mileage_entries").select("*").order("trip_date", { ascending: false })),
    readTable("income_transactions", supabase.from("income_transactions").select("*").order("income_date", { ascending: false })),
    readTable("receipts", supabase.from("receipts").select("*").order("created_at", { ascending: false })),
    readTable("receipt_line_items", supabase.from("receipt_line_items").select("*").order("created_at", { ascending: false })),
  ]);
  return { profile: profileRows[0] || null, properties, categories: dedupeCategories(categories), vendors, expenses, mileage, income, receipts, lineItems };
}

export async function upsertRow(table, row, id) {
  const payload = { ...row };
  const response = id
    ? await supabase.from(table).update(payload).eq("id", id).select("*").single()
    : await supabase.from(table).insert(payload).select("*").single();
  if (response.error) throw response.error;
  return response.data;
}

export async function deleteRow(table, id) {
  const { error } = await supabase.from(table).delete().eq("id", id);
  if (error) throw error;
}

export async function createVendorIfNeeded(userId, vendorName) {
  const name = (vendorName || "Unknown vendor").trim();
  const { data: existing } = await supabase.from("vendors").select("*").ilike("name", name).limit(1);
  if (existing?.[0]) return existing[0];
  const { data, error } = await supabase.from("vendors").insert({ user_id: userId, name }).select("*").single();
  if (error) throw error;
  return data;
}

export async function createManualExpense({ userId, form, file }) {
  let receiptId = null;
  if (file) {
    const receipt = await uploadReceiptFile({ userId, file, propertyId: form.property_id || null });
    receiptId = receipt.id;
  }
  const payload = {
    user_id: userId,
    property_id: form.property_id || null,
    vendor_id: form.vendor_id || null,
    category_id: form.category_id || null,
    receipt_id: receiptId,
    expense_date: form.expense_date || today(),
    description: form.description || "Manual expense",
    amount: Number(form.amount || 0),
    tax_amount: Number(form.tax_amount || 0),
    tip_amount: Number(form.tip_amount || 0),
    is_tax_deductible: Boolean(form.is_tax_deductible),
    is_reimbursable: Boolean(form.is_reimbursable),
    schedule_e_category: form.schedule_e_category || "",
    status: form.status || "Categorized",
    notes: form.notes || "",
  };
  return upsertRow("expenses", payload);
}

export async function uploadReceiptFile({ userId, file, propertyId }) {
  const receiptId = crypto.randomUUID();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
  const filePath = `receipts/${userId}/${receiptId}/${Date.now()}-${safeName}`;
  const upload = await supabase.storage.from("receipts").upload(filePath, file, { contentType: file.type, upsert: false });
  if (upload.error) throw upload.error;
  const { data, error } = await supabase
    .from("receipts")
    .insert({
      id: receiptId,
      user_id: userId,
      property_id: propertyId || null,
      file_path: filePath,
      original_filename: file.name,
      mime_type: file.type,
      file_size_bytes: file.size,
      extraction_status: "Pending",
      review_status: "Needs Review",
    })
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function getReceiptSignedUrl(filePath) {
  if (!filePath) return "";
  const { data, error } = await supabase.storage.from("receipts").createSignedUrl(filePath, 60 * 10);
  if (error) return "";
  return data?.signedUrl || "";
}

export function buildLookups(data) {
  const byId = (rows) => Object.fromEntries((rows || []).map((row) => [row.id, row]));
  return {
    properties: byId(data.properties),
    categories: byId(data.categories),
    vendors: byId(data.vendors),
    receipts: byId(data.receipts),
  };
}